import React, { useState } from 'react';
import BackButton from '../components/BackButton';
import Footer from '../components/Footer';
import '../App.scss';

const MultiplicationTables: React.FC = () => {
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  
  // Generate numbers 1 through 12 for table selection
  const tableNumbers = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generate the multiplication table for the selected number
  const generateTable = (num: number) => {
    return Array.from({ length: 12 }, (_, i) => {
      const multiplier = i + 1;
      const result = num * multiplier;
      return { multiplier, result };
    });
  };
  
  return (
    <div className="multiplication-tables-page">
      <div className="game-header">
        <BackButton />
      </div>
      <h1>Multiplication Tables</h1>
      
      <div className="page-content">
        <p className="intro">Learn and practice multiplication tables from 1 to 12. Click on a number to see its table!</p>
        
        <div className="table-selector">
          {tableNumbers.map(number => (
            <button 
              key={number}
              className={`table-button ${selectedTable === number ? 'active' : ''}`}
              onClick={() => setSelectedTable(number)}
            >
              {number}
            </button>
          ))}
        </div>
        
        {selectedTable && (
          <div className="table-display">
            <h2>Multiplication Table of {selectedTable}</h2>
            <div className="table-card">
              {generateTable(selectedTable).map(({ multiplier, result }) => (
                <div key={multiplier} className="multiplication-row">
                  {selectedTable} × {multiplier} = {result}
                </div>
              ))}
            </div>
            
            <div className="visual-representation">
              <h3>Visual Representation</h3>
              <div className="grid-container">
                {Array.from({ length: 12 }).map((_, colIndex) => {
                  const numDots = colIndex + 1;
                  const groupSize = numDots <= 4 ? 'small' : numDots <= 9 ? '' : 'large';
                  return (
                    <div key={colIndex} className="grid-cell">
                      <div className="cell-content">
                        <div className="groups-container">
                          {Array.from({ length: selectedTable }).map((_, groupIndex) => (
                            <div key={groupIndex} className={`group-square ${groupSize}`}>
                              {Array.from({ length: numDots }).map((_, dotIndex) => (
                                <div key={dotIndex} className="dot" />
                              ))}
                            </div>
                          ))}
                        </div>
                        <div className="cell-label">
                          {selectedTable} × {numDots} = {selectedTable * numDots}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="visual-explanation">
                Each square represents a group, and each dot inside represents an item in that group.
                For example, {selectedTable} × 3 = {selectedTable * 3} means {selectedTable} groups of 3 items each.
              </p>
            </div>
          </div>
        )}
        
        {!selectedTable && (
          <div className="instructions">
            <h2>How to Use</h2>
            <p>Click on any number above to see its multiplication table up to 12.</p>
            <p>You'll also see a visual representation to help understand the concept better!</p>
          </div>
        )}
        
        <div className="study-tips">
          <h2>Tips for Learning Multiplication Tables</h2>
          <ul>
            <li>Practice a little bit every day</li>
            <li>Try to find patterns in the numbers</li>
            <li>Visualize groups of objects to understand multiplication</li>
            <li>Use rhymes or songs to remember difficult tables</li>
            <li>Quiz yourself regularly to reinforce your learning</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MultiplicationTables; 